'use client';

import {useCallback, useState, type SyntheticEvent} from 'react';

import {thumbnailUrlCandidates} from '@/features/instructor-profile/lib/parse-youtube-id';

/**
 * YouTube answers 200 with a 120×90 grey placeholder when a named resolution
 * was never generated.  A real `hqdefault` frame is 480px wide, so anything
 * this small is not a usable thumbnail.
 */
const PLACEHOLDER_WIDTH_PX = 120;

interface ThumbnailAttempt {
    videoId: string | null;
    attempt: number;
}

export interface YouTubeThumbnail {
    /** Current candidate, or `null` once every size through `hqdefault` has failed. */
    src: string | null;
    onError: () => void;
    onLoad: (event: SyntheticEvent<HTMLImageElement>) => void;
    /** Start again at the highest resolution.  Used when the instructor embeds a new URL. */
    reset: () => void;
}

/**
 * Resolve the sharpest YouTube thumbnail that actually exists.
 *
 * Starts at `maxresdefault`, then `sddefault`, and stops at `hqdefault`.
 * A load error or YouTube's 120×90 placeholder advances to the next size.
 */
export function useYouTubeThumbnail(videoId: string | null): YouTubeThumbnail {
    const candidates = videoId ? thumbnailUrlCandidates(videoId) : [];
    const [state, setState] = useState<ThumbnailAttempt>({videoId, attempt: 0});
    const attempt = state.videoId === videoId ? state.attempt : 0;
    const exhausted = candidates.length === 0 || attempt >= candidates.length;

    const advance = useCallback(() => {
        setState((current) => {
            const currentAttempt = current.videoId === videoId ? current.attempt : 0;
            return {videoId, attempt: currentAttempt + 1};
        });
    }, [videoId]);

    const onLoad = useCallback(
        (event: SyntheticEvent<HTMLImageElement>) => {
            const {naturalWidth} = event.currentTarget;
            if (naturalWidth <= 0 || naturalWidth > PLACEHOLDER_WIDTH_PX) return;
            // Keep `hqdefault` even if it is small — it is the last resort.
            setState((current) => {
                const currentAttempt = current.videoId === videoId ? current.attempt : 0;
                if (currentAttempt + 1 >= candidates.length) return current;
                return {videoId, attempt: currentAttempt + 1};
            });
        },
        [candidates.length, videoId],
    );

    const reset = useCallback(() => {
        setState({videoId, attempt: 0});
    }, [videoId]);

    return {
        src: exhausted ? null : candidates[attempt],
        onError: advance,
        onLoad,
        reset,
    };
}
